import React, { Suspense, lazy, useEffect  } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from './components/common/Navbar';
import Footer from './components/common/Footer';
import LoadingSpinner from './components/common/LoadingSpinner';
import ErrorBoundary from './components/common/ErrorBoundary';
import ProtectedRoute from './components/common/ProtectedRoute';
import BurnerWidget from './components/common/BurnerWidget';
import { useAuth } from './hooks/useAuth';
import { pollingService as websocketService } from './services/pollingService';

// Lazy load pages
const Home = lazy(() => import('./pages/Home'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const AuctionDetail = lazy(() => import('./pages/AuctionDetail'));
const CreateAuction = lazy(() => import('./pages/CreateAuction'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const Payments = lazy(() => import('./pages/Payments'));
const EditAuction = lazy(() => import('./pages/EditAuction'));
const Auctions = lazy(() => import('./pages/Auctions'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Notifications = lazy(() => import('./pages/Notifications'));

function App() {
  const { loading, isAuthenticated, user } = useAuth();

  // Initialize WebSocket connection when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      console.log('User authenticated, connecting WebSocket for user:', user.id);
      websocketService.connect(user.id);
    } else {
      websocketService.disconnect();
    }
    
    return () => {
      websocketService.disconnect();
    };
  }, [isAuthenticated, user]);

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <ErrorBoundary>
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Navbar />
        <main className="flex-grow container mx-auto px-4 py-8">
          <AnimatePresence mode="wait">
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                {/* Public Routes - Always accessible */}
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password/:token" element={<ResetPassword />} />
                
                {/* Protected Routes - Only for authenticated users */}
                <Route element={<ProtectedRoute />}>
                  <Route path="/auctions" element={<Auctions />} />
                  <Route path="/auctions/:id" element={<AuctionDetail />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/payments" element={<Payments />} />
                  <Route path="/notifications" element={<Notifications />} />
                </Route>
                
                {/* Admin Only Routes */}
                <Route element={<ProtectedRoute adminOnly />}>
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/auctions/create" element={<CreateAuction />} />
                  <Route path="/auctions/edit/:id" element={<EditAuction />} />
                </Route>
                
                {/* Catch all - redirect to home */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </AnimatePresence>
        </main>
        <Footer />
        
        {/* Global Burner Widget - Only shows for authenticated users */}
        <BurnerWidget />
      </div>
    </ErrorBoundary>
  );
}

export default App;